import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  FilePdf,
  Globe,
  Info,
  Link as LinkIcon,
  Megaphone,
  PencilSimple,
  Question,
  ShieldCheck,
  Sparkle,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import gsap from "gsap";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { FieldShell, Input, Select, Textarea } from "../components/ui/Field";
import { useToast } from "../components/ui/Toast";
import {
  ALLOWED_UPLOADS,
  APPLICATION_STEPS,
  EVIDENCE_TYPES,
  MESSAGE_CATEGORIES,
  OPT_IN_METHODS,
} from "../config/application";
import { SAMPLE_PLACEHOLDERS } from "../config/placeholders";
import { demoApplication } from "../data/mock";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";
import {
  confirmUpload,
  createUploadUrl,
  deleteUpload,
  getAssessmentSnapshot,
  saveAnonymousApplication,
  startAnonymousAssessment,
} from "../services/api";
import { supabase } from "../services/supabase";
import type { ApplicationDraft } from "../types/assessment";
import { validateEvidenceFile } from "../utils/fileValidation";
import { validateSampleMessage } from "../utils/messageValidation";

const blank: ApplicationDraft = {
  ...demoApplication,
  id: crypto.randomUUID(),
  usesDba: false,
  legalBusinessName: "",
  brandName: "",
  businessType: "",
  industry: "",
  state: "",
  country: "United States",
  websiteUrl: "",
  optInUrl: "",
  privacyPolicyUrl: "",
  termsUrl: "",
  campaignUseCase: "Low Volume Mixed",
  campaignDescription: "",
  messageCategories: [],
  estimatedMessageFrequency: "",
  optInDiscovery: "",
  optInSteps: "",
  phoneCollectionDescription: "",
  consentCollectionDescription: "",
  postConsentDescription: "",
  consentOptionality: "",
  marketingConsent: "",
  transactionalConsent: "",
  registrationInformationConfirmed: false,
  messages: {
    sample1: "",
    sample2: "",
    sample3: "",
    confirmation: "",
    stop: "",
    help: "",
  },
  status: "draft",
  updatedAt: new Date().toISOString(),
};
type MessageKey = keyof ApplicationDraft["messages"];
interface UploadItem {
  name: string;
  progress: number;
  preview?: string;
  error?: string;
  path?: string;
}
const urlOk = (value: string) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

export function ApplicationPage() {
  const { id } = useParams();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<ApplicationDraft>(blank);
  const [hydrated, setHydrated] = useState(id !== "current");
  const [uploads, setUploads] = useState<Record<string, UploadItem>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const stepPanelRef = useRef<HTMLDivElement>(null);
  const stepHeadingRef = useRef<HTMLDivElement>(null);
  const stepDirectionRef = useRef(1);
  const transitionInProgressRef = useRef(false);
  const { credentials, startNew } = useAssessmentSession();
  const toast = useToast();
  const navigate = useNavigate();
  const set = <K extends keyof ApplicationDraft>(
    key: K,
    value: ApplicationDraft[K],
  ) =>
    setDraft((current) => ({
      ...current,
      [key]: value,
      updatedAt: new Date().toISOString(),
    }));
  useEffect(() => {
    if (credentials) return;
    void startNew();
  }, [credentials, startNew]);
  useEffect(() => {
    if (!credentials || id !== "current") return;
    if (import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true") {
      setDraft(demoApplication);
      setHydrated(true);
      return;
    }
    void getAssessmentSnapshot(credentials)
      .then((snapshot) => {
        if (snapshot.application) setDraft(snapshot.application);
        if (snapshot.uploads) {
          setUploads(
            Object.fromEntries(
              snapshot.uploads.map((upload) => [
                upload.evidenceType,
                {
                  name: upload.fileName,
                  progress: 100,
                  path: upload.storagePath,
                },
              ]),
            ),
          );
        }
      })
      .catch(() => toast.show("Previous assessment could not be loaded"))
      .finally(() => setHydrated(true));
  }, [credentials, id, toast]);
  useEffect(() => {
    if (!credentials || !hydrated) return;
    const timer = window.setTimeout(
      () =>
        void saveAnonymousApplication(credentials, draft).catch(
          () => undefined,
        ),
      1400,
    );
    return () => window.clearTimeout(timer);
  }, [credentials, draft, hydrated]);
  useLayoutEffect(() => {
    const panel = stepPanelRef.current;
    const heading = stepHeadingRef.current;
    transitionInProgressRef.current = false;
    if (
      !panel ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const context = gsap.context(() => {
      gsap.fromTo(
        panel,
        { autoAlpha: 0, x: stepDirectionRef.current * 26 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.46,
          ease: "power3.out",
          clearProps: "transform,opacity,visibility",
        },
      );
      gsap.fromTo(
        heading,
        { autoAlpha: 0, y: 9 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.38,
          ease: "power2.out",
          clearProps: "transform,opacity,visibility",
        },
      );
    });
    return () => context.revert();
  }, [step]);

  function changeStep(nextStep: number) {
    if (
      nextStep === step ||
      nextStep < 0 ||
      nextStep >= APPLICATION_STEPS.length ||
      transitionInProgressRef.current
    )
      return;
    setErrors([]);
    stepDirectionRef.current = nextStep > step ? 1 : -1;
    const panel = stepPanelRef.current;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (!panel || reducedMotion) {
      setStep(nextStep);
      window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
      return;
    }
    transitionInProgressRef.current = true;
    gsap.killTweensOf(panel);
    gsap.to(panel, {
      autoAlpha: 0,
      x: stepDirectionRef.current * -18,
      duration: 0.18,
      ease: "power2.in",
      onComplete: () => {
        setStep(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
    });
  }

  async function save(showToast = true) {
    if (!credentials) return null;
    setSaving(true);
    try {
      const { applicationId } = await saveAnonymousApplication(
        credentials,
        draft,
      );
      if (applicationId !== draft.id) {
        setDraft((current) => ({ ...current, id: applicationId }));
      }
      if (showToast) toast.show("Assessment saved securely");
      return applicationId;
    } catch (error) {
      toast.show(error instanceof Error ? error.message : "Could not save");
      return null;
    } finally {
      setSaving(false);
    }
  }
  async function next() {
    const found = validateStep(step, draft);
    setErrors(found);
    if (found.length) return;
    if (step < 8) {
      changeStep(step + 1);
      return;
    }
    if (!credentials) return;
    if (
      import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "true" &&
      !turnstileToken
    ) {
      setErrors([
        "Complete the bot protection check before running the assessment.",
      ]);
      return;
    }
    setSaving(true);
    try {
      const applicationId = await save(false);
      if (!applicationId) return;
      await startAnonymousAssessment(
        credentials,
        applicationId,
        turnstileToken,
      );
      navigate(`/assessment/${applicationId}/processing`);
    } catch (error) {
      setErrors([
        error instanceof Error ? error.message : "Unable to start assessment.",
      ]);
    } finally {
      setSaving(false);
    }
  }
  async function upload(kind: string, file?: File) {
    if (!file) return;
    const problems = validateEvidenceFile(file);
    const preview = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : undefined;
    setUploads((current) => ({
      ...current,
      [kind]: {
        name: file.name,
        progress: problems.length ? 0 : 15,
        preview,
        error: problems[0],
      },
    }));
    if (problems.length) return;
    if (!credentials || import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true") {
      window.setTimeout(
        () =>
          setUploads((current) => ({
            ...current,
            [kind]: { ...current[kind]!, progress: 100 },
          })),
        450,
      );
      return;
    }
    try {
      const applicationId = await save(false);
      if (!applicationId) throw new Error("Application could not be saved.");
      const signed = await createUploadUrl(credentials, {
        applicationId,
        evidenceType: kind,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      });
      setUploads((current) => ({
        ...current,
        [kind]: { ...current[kind]!, progress: 60 },
      }));
      const { error } = await supabase.storage
        .from("a2p-evidence")
        .uploadToSignedUrl(signed.path, signed.token, file, {
          contentType: file.type,
        });
      if (error) throw error;
      await confirmUpload(credentials, signed.path);
      setUploads((current) => ({
        ...current,
        [kind]: { ...current[kind]!, progress: 100, path: signed.path },
      }));
      toast.show("Evidence uploaded privately");
    } catch (error) {
      setUploads((current) => ({
        ...current,
        [kind]: {
          ...current[kind]!,
          progress: 0,
          error: error instanceof Error ? error.message : "Upload failed.",
        },
      }));
    }
  }
  async function removeUpload(kind: string) {
    const item = uploads[kind];
    if (
      credentials &&
      item?.path &&
      import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "true"
    ) {
      try {
        await deleteUpload(credentials, item.path);
      } catch (error) {
        toast.show(
          error instanceof Error ? error.message : "Could not remove upload",
        );
        return;
      }
    }
    if (item?.preview) URL.revokeObjectURL(item.preview);
    setUploads((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
  }

  return (
    <main className="mx-auto max-w-[1320px] px-4 py-6 sm:px-8 sm:py-8">
      <div ref={stepHeadingRef} className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Guided assessment</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
            {APPLICATION_STEPS[step]}
          </h1>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold">
            Step {step + 1} of {APPLICATION_STEPS.length}
          </p>
          <p className="mt-1 text-xs text-[#70777B]">
            {saving ? "Saving changes…" : "Saved automatically"}
          </p>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-9 gap-1.5 lg:hidden">
        {APPLICATION_STEPS.map((label, index) => (
          <button
            key={label}
            aria-label={`Go to ${label}`}
            onClick={() => changeStep(index)}
            className={`h-1.5 rounded-full ${index <= step ? "bg-brand" : "bg-[#25292B]"}`}
          />
        ))}
      </div>
      <div className="dashboard-command-strip mb-5 hidden items-center gap-2 lg:flex">
        <span>Anonymous session</span>
        <span>Auto-save on</span>
        <span>9-step review</span>
        <strong className="ml-auto">{APPLICATION_STEPS[step]}</strong>
      </div>
      <div className="grid gap-5 lg:grid-cols-[230px_1fr]">
        <aside className="hidden lg:block">
          <Card className="sticky top-6 p-3.5">
            <ol className="relative space-y-2">
              {APPLICATION_STEPS.map((label, index) => {
                const isLast = index === APPLICATION_STEPS.length - 1;
                const isCompleted = index < step;
                const isCurrent = index === step;

                return (
                  <li key={label} className="relative">
                    {!isLast && (
                      <div
                        className={`stepper-connector-line ${
                          isCompleted ? "is-active" : ""
                        }`}
                      />
                    )}
                    <button
                      onClick={() => changeStep(index)}
                      className={`relative z-[1] flex w-full items-center gap-3.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-all duration-200 ${
                        isCurrent
                          ? "bg-brand/10 text-brand font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_0_16px_rgba(168,230,29,0.08)] border border-brand/25"
                          : isCompleted
                          ? "text-[#F5F7F7] hover:bg-white/[.04]"
                          : "text-[#70777B] hover:text-[#A0A5A8] hover:bg-white/[.02]"
                      }`}
                    >
                      <span
                        className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border text-xs font-bold transition-all duration-200 ${
                          isCompleted
                            ? "border-brand bg-brand text-ink shadow-[0_0_10px_rgba(168,230,29,0.3)]"
                            : isCurrent
                            ? "border-brand bg-brand/20 text-brand shadow-[0_0_12px_rgba(168,230,29,0.4)]"
                            : "border-white/10 bg-white/[.03] text-[#70777B]"
                        }`}
                      >
                        {isCompleted ? (
                          <Check size={14} weight="bold" />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <div className="flex flex-col">
                        <span className="leading-tight">{label}</span>
                        {isCurrent && (
                          <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-brand/80">
                            In progress
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </Card>
        </aside>
        <Card className="bg-[#111314] p-5 sm:p-8">
          <div ref={stepPanelRef} data-step-panel>
            <StepContent
              step={step}
              draft={draft}
              set={set}
              uploads={uploads}
              onUpload={upload}
              onRemove={removeUpload}
              onEdit={changeStep}
            />
            {step === 8 &&
              import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "true" && (
                <div className="liquid-glass-subtle mt-7 rounded-2xl border border-white/[.07] p-5">
                  <p className="mb-4 text-sm font-semibold">Bot protection</p>
                  <TurnstileWidget onToken={setTurnstileToken} />
                </div>
              )}
            {errors.length > 0 && (
              <div
                className="mt-6 rounded-2xl border border-red-500/25 bg-red-500/[.08] p-4"
                role="alert"
              >
                <p className="font-semibold text-red-200">
                  Please review this step
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-300">
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-8 flex items-center justify-between border-t border-white/[.07] pt-6">
            <Button
              variant="ghost"
              disabled={step === 0}
              onClick={() => changeStep(step - 1)}
            >
              <ArrowLeft size={17} />
              Back
            </Button>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="hidden sm:flex"
                onClick={() => void save()}
              >
                Save & continue later
              </Button>
              <Button onClick={() => void next()} disabled={saving}>
                {step === 8 ? "Run A2P assessment" : "Continue"}
                <ArrowRight size={17} weight="bold" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
      <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-5 text-[#70777B]">
        Assessment data and uploaded evidence are stored temporarily and may be
        automatically deleted after the retention period.
      </p>
    </main>
  );
}

function StepContent({
  step,
  draft,
  set,
  uploads,
  onUpload,
  onRemove,
  onEdit,
}: {
  step: number;
  draft: ApplicationDraft;
  set: <K extends keyof ApplicationDraft>(
    key: K,
    value: ApplicationDraft[K],
  ) => void;
  uploads: Record<string, UploadItem>;
  onUpload: (kind: string, file?: File) => Promise<void>;
  onRemove: (kind: string) => Promise<void>;
  onEdit: (step: number) => void;
}) {
  const activeMessage = useRef<HTMLTextAreaElement | null>(null);
  const activeKey = useRef<MessageKey>("sample1");
  const [showDbaHelp, setShowDbaHelp] = useState(false);
  const input =
    (key: keyof ApplicationDraft) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) =>
      set(key, event.target.value as never);
  function insertPlaceholder(value: string) {
    const target = activeMessage.current;
    const key = activeKey.current;
    const current = draft.messages[key];
    const start = target?.selectionStart ?? current.length;
    const end = target?.selectionEnd ?? start;
    const next = `${current.slice(0, start)}${value}${current.slice(end)}`;
    set("messages", { ...draft.messages, [key]: next });
    window.setTimeout(() => {
      target?.focus();
      target?.setSelectionRange(start + value.length, start + value.length);
    }, 0);
  }
  if (step === 0)
    return (
      <Step
        title="Confirm the campaign scope"
        intro="Every assessment uses the Low Volume Mixed campaign type. Select DBA only if the business operates under another public-facing name."
      >
        <div className="grid items-start gap-4 sm:grid-cols-2">
          <Choice
            selected={draft.registrationType === "low_volume_mixed"}
            onClick={() => set("registrationType", "low_volume_mixed")}
            title="Low Volume Mixed"
            body="For lower-volume campaigns that combine supported conversational, informational, and promotional messages."
          />
          <div className={`liquid-glass-subtle rounded-[22px] border p-5 ${draft.usesDba ? "dashboard-selected border-brand/60" : "border-white/[.1]"}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="dashboard-tag">Optional</span>
                <h3 className="mt-5 text-lg font-semibold">This business uses a DBA</h3>
              </div>
              <button
                type="button"
                aria-label="What is a DBA?"
                aria-expanded={showDbaHelp}
                aria-controls="dba-explanation"
                onClick={() => setShowDbaHelp((visible) => !visible)}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/[.14] bg-white/[.055] text-brand transition hover:border-brand/40 hover:bg-brand/10 focus:outline-none focus:ring-2 focus:ring-brand/60"
              >
                <Question size={18} weight="bold" />
              </button>
            </div>
            <p className="mt-3 text-sm leading-6 text-[#A0A5A8]">
              Select this only when customers know the business by a different
              name than its legal registered name.
            </p>
            <button
              type="button"
              aria-pressed={draft.usesDba}
              onClick={() => {
                set("usesDba", !draft.usesDba);
                if (draft.usesDba) set("brandName", "");
              }}
              className={`liquid-glass-button mt-5 inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold ${draft.usesDba ? "border-brand/40 text-brand" : "text-[#C4CBC7]"}`}
            >
              <span className={`grid h-5 w-5 place-items-center rounded-full border ${draft.usesDba ? "border-brand bg-brand text-ink" : "border-white/20"}`}>
                {draft.usesDba ? <Check size={12} weight="bold" /> : null}
              </span>
              {draft.usesDba ? "DBA selected" : "Select DBA"}
            </button>
            {showDbaHelp ? (
              <div id="dba-explanation" className="mt-5 rounded-2xl border border-brand/15 bg-brand/[.055] p-4 text-sm leading-6 text-[#C8CECB]">
                <strong className="text-white">What does DBA mean?</strong>
                <p className="mt-1">
                  DBA means “doing business as.” It is the name customers see
                  when it differs from the company's official legal name.
                </p>
                <p className="mt-3 text-[#A0A5A8]">
                  <strong className="text-[#D9DEDB]">Example:</strong> The legal
                  company is “Smith Holdings LLC,” but its website and texts say
                  “BrightPath Plumbing.” BrightPath Plumbing is the DBA.
                </p>
                <p className="mt-3 text-[#A0A5A8]">
                  If the legal name and the name customers see are the same,
                  leave DBA unselected.
                </p>
              </div>
            ) : null}
          </div>
        </div>
        <p className="mt-5 text-sm leading-6 text-[#70777B]">
          Other A2P use cases and registration paths are outside this version's
          assessment scope.
        </p>
      </Step>
    );
  if (step === 1)
    return (
      <Step
        title="Business information"
        intro="Enter public business details exactly as they will appear in the registration."
      >
        <Grid>
          <FieldShell label="Legal business name">
            <Input
              value={draft.legalBusinessName}
              onChange={input("legalBusinessName")}
            />
          </FieldShell>
          {draft.usesDba ? (
            <FieldShell
              label="DBA name"
              hint="Use the customer-facing name shown consistently on the website, forms, and messages."
            >
              <Input value={draft.brandName} onChange={input("brandName")} />
            </FieldShell>
          ) : null}
          <FieldShell label="Business type">
            <Select value={draft.businessType} onChange={input("businessType")}>
              <option value="">Select type</option>
              <option>LLC</option>
              <option>Corporation</option>
              <option>Nonprofit</option>
              <option>Sole Proprietorship</option>
              <option>Other</option>
            </Select>
          </FieldShell>
          <FieldShell label="Industry">
            <Input value={draft.industry} onChange={input("industry")} />
          </FieldShell>
          <FieldShell label="State / region">
            <Input value={draft.state} onChange={input("state")} />
          </FieldShell>
          <FieldShell label="Country">
            <Input value={draft.country} onChange={input("country")} />
          </FieldShell>
          <FieldShell
            label="Business website"
            hint="Use the public HTTPS website for this brand."
          >
            <Input
              type="url"
              placeholder="https://example.com"
              value={draft.websiteUrl}
              onChange={input("websiteUrl")}
            />
          </FieldShell>
        </Grid>
        <label className="liquid-glass-subtle mt-6 flex gap-3 rounded-2xl border border-white/[.07] p-4 text-sm leading-6">
          <input
            type="checkbox"
            checked={draft.registrationInformationConfirmed}
            onChange={(event) =>
              set("registrationInformationConfirmed", event.target.checked)
            }
            className="mt-1 h-4 w-4 accent-[#A8E61D]"
          />
          <span>
            I confirm that the legal business name and EIN that will be entered
            in GoHighLevel will exactly match the business's official
            registration records.
            <small className="mt-1 block text-[#70777B]">
              Only this confirmation is stored. Never enter an EIN, SSN, tax ID,
              government ID, or banking information here.
            </small>
          </span>
        </label>
      </Step>
    );
  if (step === 2)
    return (
      <Step
        title="Website information"
        intro="Add the exact public pages a reviewer should be able to open."
      >
        <Grid>
          {(
            [
              [
                "websiteUrl",
                "Main website URL",
                "Your primary public website.",
              ],
              [
                "optInUrl",
                "Contact / opt-in form URL",
                "The page where the phone number and consent are collected.",
              ],
              [
                "privacyPolicyUrl",
                "Privacy Policy URL",
                "Use the direct HTTPS link to the policy.",
              ],
              [
                "termsUrl",
                "Terms of Service URL",
                "Use the direct HTTPS link to the terms.",
              ],
            ] as const
          ).map(([key, label, hint]) => (
            <FieldShell key={key} label={label} hint={hint}>
              <Input
                type="url"
                placeholder="https://example.com"
                value={draft[key]}
                onChange={input(key)}
              />
            </FieldShell>
          ))}
        </Grid>
      </Step>
    );
  if (step === 3)
    return (
      <Step
        title="Campaign information"
        intro="Describe what messages contacts will receive and how often."
      >
        <Grid>
          <FieldShell label="Campaign use case" hint="Fixed for this assessment version.">
            <Input value="Low Volume Mixed" readOnly aria-readonly="true" />
          </FieldShell>
          <FieldShell label="Estimated message frequency">
            <Input
              placeholder="Up to 4 messages per month"
              value={draft.estimatedMessageFrequency}
              onChange={input("estimatedMessageFrequency")}
            />
          </FieldShell>
        </Grid>
        <div className="mt-5">
          <FieldShell
            label="Campaign description"
            currentLength={draft.campaignDescription.length}
            maxLength={1024}
          >
            <Textarea
              className="min-h-36"
              value={draft.campaignDescription}
              onChange={input("campaignDescription")}
            />
          </FieldShell>
        </div>
        <fieldset className="mt-6">
          <legend className="text-sm font-medium">Message categories</legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {MESSAGE_CATEGORIES.map((category) => (
              <button
                type="button"
                key={category}
                onClick={() =>
                  set(
                    "messageCategories",
                    draft.messageCategories.includes(category)
                      ? draft.messageCategories.filter(
                          (value) => value !== category,
                        )
                      : [...draft.messageCategories, category],
                  )
                }
                className={`rounded-full border px-4 py-2 text-sm transition ${draft.messageCategories.includes(category) ? "border-brand/50 bg-brand/10 text-brand" : "border-white/[.08] bg-[#171A1C] text-[#A0A5A8] hover:border-white/20"}`}
              >
                {category}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <YesNo
            label="Contains links"
            value={draft.containsLinks}
            onChange={(value) => set("containsLinks", value)}
          />
          <YesNo
            label="Contains phone numbers"
            value={draft.containsPhoneNumbers}
            onChange={(value) => set("containsPhoneNumbers", value)}
          />
          <YesNo
            label="Age-gated content"
            value={draft.ageGatedContent}
            onChange={(value) => set("ageGatedContent", value)}
          />
        </div>
      </Step>
    );
  if (step === 4)
    return (
      <Step
        title="Opt-in method"
        intro="Select the primary method, then describe the complete consent journey."
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {OPT_IN_METHODS.map((method, index) => (
            <button
              type="button"
              key={method}
              onClick={() => set("optInMethod", method)}
              className={`liquid-glass-subtle min-h-28 rounded-2xl border p-4 text-left transition ${draft.optInMethod === method ? "border-brand/50 bg-brand/10" : "border-white/[.07] hover:border-white/20"}`}
            >
              <span
                className={
                  draft.optInMethod === method ? "text-brand" : "text-[#70777B]"
                }
              >
                {index < 2 ? (
                  <Globe size={22} />
                ) : index < 5 ? (
                  <LinkIcon size={22} />
                ) : (
                  <Megaphone size={22} />
                )}
              </span>
              <span className="mt-5 block text-sm font-semibold">{method}</span>
            </button>
          ))}
        </div>
        <div className="mt-7 grid gap-5">
          {(
            [
              ["optInDiscovery", "How does the contact discover the opt-in?"],
              ["optInSteps", "What steps does the contact take?"],
              [
                "phoneCollectionDescription",
                "How do they provide their phone number?",
              ],
              ["consentCollectionDescription", "How is consent collected?"],
              ["postConsentDescription", "What happens after consent?"],
              [
                "consentOptionality",
                "Is consent optional or required for the main service?",
              ],
            ] as const
          ).map(([key, label]) => (
            <FieldShell key={key} label={label}>
              <Textarea value={draft[key]} onChange={input(key)} />
            </FieldShell>
          ))}
        </div>
      </Step>
    );
  if (step === 5)
    return (
      <Step
        title="Consent wording"
        intro="Keep marketing and non-marketing consent separate and easy to review."
      >
        <div className="grid gap-5">
          <FieldShell
            label="Marketing consent checkbox"
            currentLength={draft.marketingConsent.length}
          >
            <Textarea
              className="min-h-40"
              value={draft.marketingConsent}
              onChange={input("marketingConsent")}
            />
          </FieldShell>
          <FieldShell
            label="Non-marketing / transactional consent checkbox"
            currentLength={draft.transactionalConsent.length}
          >
            <Textarea
              className="min-h-40"
              value={draft.transactionalConsent}
              onChange={input("transactionalConsent")}
            />
          </FieldShell>
        </div>
        <div className="liquid-glass-subtle mt-6 rounded-2xl border border-white/[.07] p-5">
          <p className="text-sm font-semibold">
            Contextual readiness checklist
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              "Checkboxes are not pre-selected",
              "Message categories are described",
              "Privacy Policy is visible",
              "Terms are visible",
              "Frequency is disclosed where applicable",
              "Rates and STOP / HELP language are included where applicable",
            ].map((label) => (
              <span
                key={label}
                className="flex items-start gap-2 text-sm leading-5 text-[#A0A5A8]"
              >
                <CheckCircle size={17} className="mt-0.5 shrink-0 text-brand" />
                {label}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#70777B]">
            General preparation guidance only; current approved documentation
            and internal templates control future assessment decisions.
          </p>
        </div>
      </Step>
    );
  if (step === 6)
    return (
      <Step
        title="Sample messages"
        intro="Use bracketed placeholders for every customer-specific value."
      >
        <div className="liquid-glass-subtle mb-6 rounded-2xl border border-white/[.09] p-4.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-[#70777B]">
              Quick placeholder insertion
            </p>
            <span className="text-xs text-[#70777B]">Click to insert at active field cursor</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {SAMPLE_PLACEHOLDERS.map((placeholder) => (
              <button
                type="button"
                key={placeholder}
                onClick={() => insertPlaceholder(placeholder)}
                className="placeholder-chip"
              >
                <Sparkle size={13} weight="fill" className="shrink-0 text-brand" />
                <span>{placeholder}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-5">
          {(
            [
              ["sample1", "Sample Message 1"],
              ["sample2", "Sample Message 2"],
              ["sample3", "Sample Message 3"],
              ["confirmation", "Opt-In Confirmation Message"],
              ["stop", "STOP Response"],
              ["help", "HELP Response"],
            ] as const
          ).map(([key, label]) => {
            const warning = validateSampleMessage(
              draft.messages[key],
              draft.usesDba ? draft.brandName : draft.legalBusinessName,
            )[0];
            return (
              <FieldShell
                key={key}
                label={label}
                error={warning?.message}
                currentLength={draft.messages[key].length}
                maxLength={160}
              >
                <Textarea
                  ref={(element) => {
                    if (activeKey.current === key)
                      activeMessage.current = element;
                  }}
                  onFocus={(event) => {
                    activeKey.current = key;
                    activeMessage.current = event.currentTarget;
                  }}
                  className="min-h-32"
                  value={draft.messages[key]}
                  onChange={(event) =>
                    set("messages", {
                      ...draft.messages,
                      [key]: event.target.value,
                    })
                  }
                />
              </FieldShell>
            );
          })}
        </div>
      </Step>
    );
  if (step === 7)
    return (
      <Step
        title="Upload supporting evidence"
        intro="Add clear screenshots of the opt-in experience and policy pages."
      >
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-500/20 bg-red-500/[.06] p-4 text-sm leading-6 text-red-100">
          <Info size={20} className="mt-0.5 shrink-0 text-red-300" />
          <p>
            Do not upload unredacted tax documents, government identification,
            banking information, signatures, Social Security numbers, EIN
            documents, or other sensitive personal information.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {EVIDENCE_TYPES.map((kind) => {
            const item = uploads[kind];
            return (
              <div
                key={kind}
                className={`upload-dropzone ${
                  item?.error
                    ? "border-red-500/40 bg-red-500/[.04]"
                    : item?.progress === 100
                    ? "choice-card-selected border-brand/50"
                    : ""
                }`}
              >
                {item ? (
                  <div>
                    {item.preview ? (
                      <div className="relative mb-4 overflow-hidden rounded-xl border border-white/10">
                        <img
                          src={item.preview}
                          alt=""
                          className="h-36 w-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                        <span className="absolute top-2.5 right-2.5 rounded-full border border-white/20 bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur-md">
                          Preview
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4 grid h-24 place-items-center rounded-xl bg-white/[.03] border border-white/10 text-brand">
                        <FilePdf size={36} weight="duotone" />
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#F5F7F7]">
                          {item.name}
                        </p>
                        <p
                          className={`mt-1 text-xs font-medium ${
                            item.error
                              ? "text-red-300"
                              : item.progress === 100
                              ? "text-brand"
                              : "text-[#70777B]"
                          }`}
                        >
                          {item.error ??
                            (item.progress === 100
                              ? "✓ Uploaded & verified privately"
                              : `Uploading · ${item.progress}%`)}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${kind}`}
                        onClick={() => void onRemove(kind)}
                        className="rounded-full p-2 text-[#70777B] transition-colors hover:bg-white/[.08] hover:text-white"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-[#25292B]">
                      <div
                        className={`h-full transition-all duration-300 ${
                          item.error
                            ? "bg-red-400"
                            : "bg-brand shadow-[0_0_10px_rgba(168,230,29,0.5)]"
                        }`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                    <label className="mt-4 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-brand transition-colors hover:text-brand/80">
                      <input
                        type="file"
                        className="sr-only"
                        accept={ALLOWED_UPLOADS.join(",")}
                        onChange={(event) =>
                          void onUpload(kind, event.target.files?.[0])
                        }
                      />
                      Replace evidence file
                    </label>
                  </div>
                ) : (
                  <label className="block cursor-pointer text-center">
                    <input
                      type="file"
                      className="sr-only"
                      accept={ALLOWED_UPLOADS.join(",")}
                      onChange={(event) =>
                        void onUpload(kind, event.target.files?.[0])
                      }
                    />
                    <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl border border-brand/20 bg-brand/10 text-brand shadow-[0_0_15px_rgba(168,230,29,0.1)] transition-transform duration-200 hover:scale-105">
                      <UploadSimple size={22} weight="bold" />
                    </div>
                    <p className="mt-4 text-sm font-semibold text-[#F5F7F7]">{kind}</p>
                    <p className="mt-1.5 text-xs leading-5 text-[#70777B]">
                      Drag and drop or select file
                    </p>
                    <div className="mt-3 flex items-center justify-center gap-1.5">
                      {["PNG", "JPG", "WEBP", "PDF"].map((fmt) => (
                        <span
                          key={fmt}
                          className="rounded border border-white/10 bg-white/[.03] px-1.5 py-0.5 text-[10px] font-bold text-[#A0A5A8]"
                        >
                          {fmt}
                        </span>
                      ))}
                      <span className="text-[10px] text-[#70777B]">· 10 MB max</span>
                    </div>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </Step>
    );
  return <ReviewStep draft={draft} uploads={uploads} onEdit={onEdit} />;
}

function ReviewStep({
  draft,
  uploads,
  onEdit,
}: {
  draft: ApplicationDraft;
  uploads: Record<string, UploadItem>;
  onEdit: (step: number) => void;
}) {
  const sections = [
    {
      title: "Business",
      step: 1,
      summary: draft.usesDba
        ? `${draft.legalBusinessName || "Not provided"} · DBA: ${draft.brandName || "Not provided"}`
        : `${draft.legalBusinessName || "Not provided"} · No DBA`,
    },
    { title: "Website", step: 2, summary: draft.websiteUrl || "Not provided" },
    {
      title: "Campaign",
      step: 3,
      summary: draft.campaignUseCase || "Not provided",
    },
    { title: "Opt-in", step: 4, summary: draft.optInMethod || "Not provided" },
    {
      title: "Consent",
      step: 5,
      summary:
        draft.marketingConsent || draft.transactionalConsent
          ? "Consent wording added"
          : "Not provided",
    },
    {
      title: "Sample messages",
      step: 6,
      summary: `${Object.values(draft.messages).filter(Boolean).length} of 6 completed`,
    },
    {
      title: "Evidence",
      step: 7,
      summary: `${Object.keys(uploads).length} files added`,
    },
  ];
  return (
    <Step
      title="Review your submission"
      intro="Check each section before running the assessment."
    >
      <div className="space-y-3">
        {sections.map((section) => (
          <div
            key={section.title}
            className="liquid-glass-subtle flex items-center gap-4 rounded-2xl border border-white/[.07] p-4"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
              <Check size={17} weight="bold" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{section.title}</p>
              <p className="mt-1 truncate text-sm text-[#70777B]">
                {section.summary}
              </p>
            </div>
            <Button
              variant="ghost"
              className="px-3"
              onClick={() => onEdit(section.step)}
            >
              <PencilSimple size={16} />
              Edit
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-7 rounded-2xl border border-brand/20 bg-brand/[.06] p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={24} className="shrink-0 text-brand" />
          <div>
            <p className="font-semibold">Ready to run the assessment</p>
            <p className="mt-2 text-sm leading-6 text-[#A0A5A8]">
              The assessment will review these details against deterministic
              checks, approved sources, relevant internal templates, and the
              future assessment Worker.
            </p>
          </div>
        </div>
      </div>
    </Step>
  );
}
function Step({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
        {title}
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-6 text-[#A0A5A8]">{intro}</p>
      <div className="mt-7">{children}</div>
    </section>
  );
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-5 sm:grid-cols-2">{children}</div>;
}
function Choice({
  selected,
  onClick,
  title,
  body,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  body: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`liquid-glass-subtle min-h-44 rounded-[22px] border p-5 text-left transition-all duration-200 hover:-translate-y-0.5 ${selected ? "choice-card-selected border-brand/60" : "border-white/[.07] hover:border-white/25 hover:bg-white/[.03]"}`}
    >
      <div className="flex items-center justify-between">
        <strong className="text-lg font-semibold">{title}</strong>
        <span
          className={`grid h-6 w-6 place-items-center rounded-full border transition-all duration-200 ${selected ? "border-brand bg-brand text-ink shadow-[0_0_10px_rgba(168,230,29,0.4)]" : "border-white/20 bg-white/[.03]"}`}
        >
          {selected && <Check size={14} weight="bold" />}
        </span>
      </div>
      <p className="mt-12 max-w-xs text-sm leading-6 text-[#A0A5A8]">{body}</p>
    </button>
  );
}
function YesNo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="liquid-glass-subtle rounded-2xl border border-white/[.07] p-4">
      <p className="text-sm font-medium">{label}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[true, false].map((option) => (
          <button
            type="button"
            key={String(option)}
            onClick={() => onChange(option)}
            className={`liquid-glass-button rounded-full px-3 py-2 text-xs font-semibold transition-transform hover:scale-[1.03] active:scale-[.97] ${value === option ? "bg-brand text-ink" : "bg-white/[.045] text-[#A0A5A8]"}`}
          >
            {option ? "Yes" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}
function validateStep(step: number, draft: ApplicationDraft) {
  const errors: string[] = [];
  if (step === 1) {
    if (!draft.legalBusinessName.trim())
      errors.push("Legal business name is required.");
    if (draft.usesDba && !draft.brandName.trim())
      errors.push("DBA name is required when DBA is selected.");
    if (!draft.registrationInformationConfirmed)
      errors.push(
        "Confirm that registration information will match official records.",
      );
  }
  if (step === 2) {
    for (const [value, label] of [
      [draft.websiteUrl, "Main website"],
      [draft.optInUrl, "Opt-in URL"],
      [draft.privacyPolicyUrl, "Privacy Policy"],
      [draft.termsUrl, "Terms"],
    ] as const)
      if (!urlOk(value)) errors.push(`${label} must be a valid HTTPS URL.`);
  }
  if (step === 3) {
    if (!draft.campaignDescription.trim())
      errors.push("Campaign description is required.");
    if (!draft.messageCategories.length)
      errors.push("Select at least one message category.");
  }
  if (step === 4 && !draft.optInMethod) errors.push("Select an opt-in method.");
  if (step === 5 && !draft.marketingConsent && !draft.transactionalConsent)
    errors.push("Add at least one consent disclosure.");
  if (
    step === 6 &&
    Object.values(draft.messages).some((value) => !value.trim())
  )
    errors.push("Complete all six sample-message fields.");
  return errors;
}
